import { useEffect, useState, useContext, useMemo, useCallback, useRef } from 'react'
import { HexColorPicker } from "react-colorful"
import _ from 'lodash'

import { Tabs, Tab, Typography, Button, Box, Switch, Stack, RadioGroup, Radio, FormControlLabel, TextField, Slide, Select, MenuItem, IconButton, Tooltip } from '@mui/material'
import AppleIcon from '@mui/icons-material/Apple'
import AndroidIcon from '@mui/icons-material/Android'
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined'

import { AppContext } from '/providers/app-context'
import { PassContext } from './pass-context'
import { searchAppleAppstore, searchGooglePlaystore } from '/providers/templates-api'
import AppleFront from './apple/front'
import GoogleFront from './google/front'
import countries from './countries.json'
import 'eyedropper-polyfill'
import ColorizeIcon from '@mui/icons-material/Colorize'
import logoTextColourSource from '/components/editor/logo-text-colour-source'
import appleDataSelector from './apple/data-selector'
import StyledPaper from '../paper'
import { enqueueSnackbar } from 'notistack'

const Settings = () => {

  const { smartixPassType, applePassType, appleSubType, googlePassType, variables, setVariables, template, setTemplate, model, setModel, classData, setClassData, objectData, setObjectData, metaData, setMetaData, openVariablesManager } = useContext(PassContext)

  const [eyeDropperActive, setEyeDropperActive] = useState(false)
  const [tabIndex, setTabIndex] = useState("colours")
  const [newTemplate, setNewTemplate] = useState()
  const [newModel, setNewModel] = useState()
  const [newClassData, setNewClassData] = useState()
  const [newObjectData, setNewObjectData] = useState()
  const [newVariables, setNewVariables] = useState()
  const [newMetaData, setNewMetaData] = useState()

  const [newTemplateCopy, setNewTemplateCopy] = useState()
  const [newModelCopy, setNewModelCopy] = useState()
  const [newClassDataCopy, setNewClassDataCopy] = useState()
  const [newObjectDataCopy, setNewObjectDataCopy] = useState()
  const [newMetaDataCopy, setNewMetaDataCopy] = useState()
  const [newVariablesCopy, setNewVariablesCopy] = useState()

  const [saveEnabled, setSaveEnabled] = useState(false)
  const [reset, setReset] = useState(0)

  useEffect(() => {
    if (template) {
      const t = _.cloneDeep(template)

      if (t.meta.sharing ?? true) _.set(t, "meta.sharing", true)

      setNewTemplate(t)
      setNewTemplateCopy(_.cloneDeep(t))
    }
  }, [template, reset])

  useEffect(() => {
    if (model) {
      const m = _.cloneDeep(model)
      setNewModel(m)
      setNewModelCopy(_.cloneDeep(m))
    }
  }, [model, reset])

  useEffect(() => {
    if (classData) {
      const c = _.cloneDeep(classData)

      c.reviewStatus = "UNDER_REVIEW" // Must always go back to UNDER_REVIEW if modified
      if (!!c.multipleDevicesAndHoldersAllowedStatus) _.set(c, "multipleDevicesAndHoldersAllowedStatus", "MULTIPLE_HOLDERS")

      setNewClassData(c)
      setNewClassDataCopy(_.cloneDeep(c))
    }
  }, [classData, reset])

  useEffect(() => {
    if (objectData) {
      const o = _.cloneDeep(objectData)

      if (!!o.passConstraints?.screenshotEligible) _.set(o, "passConstraints.screenshotEligible", "ELIGIBLE")

      setNewObjectData(o)
      setNewObjectDataCopy(_.cloneDeep(o))
    }
  }, [objectData, reset])

  useEffect(() => {
    if (metaData) {
      const m = _.cloneDeep(metaData)

      setNewMetaData(m)
      setNewMetaDataCopy(_.cloneDeep(m))
    }
  }, [metaData, reset])

  useEffect(() => {
    if (variables) {
      const i = _.cloneDeep(variables)

      setNewVariables(i)
      setNewVariablesCopy(_.cloneDeep(i))
    }
  }, [variables, reset])

  useEffect(() => {
    let changed = false

    if (!_.isEqual(newTemplate, newTemplateCopy)) changed = true
    if (!_.isEqual(newModel, newModelCopy)) changed = true
    if (!_.isEqual(newMetaData, newMetaDataCopy)) changed = true
    if (!_.isEqual(newClassData, newClassDataCopy)) changed = true
    if (!_.isEqual(newObjectData, newObjectDataCopy)) changed = true
    if (!_.isEqual(newVariables, newVariablesCopy)) changed = true

    setSaveEnabled(changed)
  }, [newTemplate, newModel, newMetaData, newClassData, newObjectData, newVariables])

  const cancelEdits = () => {
    setReset(reset + 1)
  }

  const save = () => {
    if (!_.isEqual(newTemplate, newTemplateCopy)) setTemplate(newTemplate)
    if (!_.isEqual(newModel, newModelCopy)) setModel(newModel)
    if (!_.isEqual(newMetaData, newMetaDataCopy)) setMetaData(newMetaData)
    if (!_.isEqual(newClassData, newClassDataCopy)) setClassData(newClassData)
    if (!_.isEqual(newObjectData, newObjectDataCopy)) setObjectData(newObjectData)
    if (!_.isEqual(newVariables, newVariablesCopy)) setVariables(newVariables)
  }

  return (
    <>
      <StyledPaper height="calc(100vh - 200px)">
        <div className="flex flex-col md:flex-row justify-between items-center gap-2">

          <Tabs value={tabIndex} onChange={(e, value) => setTabIndex(value)} className='mb-5'>
            <Tab label="Colours" value="colours" />
            <Tab label="Meta Data" value="meta" />
            <Tab label="Apps" value="apps" />
          </Tabs>
          <IconButton onClick={openVariablesManager} sx={{ mt: -3 }}><Tooltip title="Manage Variables"><DatasetOutlinedIcon /></Tooltip></IconButton>
        </div>

        <Box sx={{ height: "calc(100vh - 350px)", overflowY: "scroll" }} >
          <MetaData tabIndex={tabIndex} newTemplate={newTemplate} setNewTemplate={setNewTemplate} newClassData={newClassData} setNewClassData={setNewClassData} newObjectData={newObjectData} setNewObjectData={setNewObjectData} newMetaData={newMetaData} setNewMetaData={setNewMetaData} smartixPassType={smartixPassType} />
          <Colours tabIndex={tabIndex} setEyeDropperActive={setEyeDropperActive} applePassType={applePassType} template={template} newVariables={newVariables} setNewVariables={setNewVariables} />
          <Apps tabIndex={tabIndex} newTemplate={newTemplate} setNewTemplate={setNewTemplate} newClassData={newClassData} setNewClassData={setNewClassData} newObjectData={newObjectData} setNewObjectData={setNewObjectData} googlePassType={googlePassType} />
        </Box>

        <div className="flex flex-row w-full justify-between absolute bottom-0 left-0 right-0 p-5 h-[75px]">
          <Button variant="outlined" disabled={!saveEnabled} onClick={cancelEdits}>Discard changes</Button>
          <Button variant="contained" disabled={!saveEnabled} onClick={save}>Apply</Button>
        </div>

      </StyledPaper>
      <ColourPreview tabIndex={tabIndex} smartixPassType={smartixPassType} applePassType={applePassType} appleSubType={appleSubType} googlePassType={googlePassType} newVariables={newVariables} newVariablesCopy={newVariablesCopy} newTemplate={newTemplate} newModel={newModel} newModelCopy={newModelCopy} newClassData={newClassData} newObjectData={newObjectData} eyeDropperActive={eyeDropperActive} />
    </>
  )
}

export default Settings

const setValue = (setter, field, value) => setter(prev => {
  let temp = { ...prev }
  _.set(temp, field, value)
  return temp
})

const ColourPreview = ({ tabIndex, newVariables, newVariablesCopy, smartixPassType, applePassType, appleSubType, googlePassType, newTemplate, newModelCopy, newModel, newClassData, newObjectData, eyeDropperActive }) => {
  const [previewDevice, setPreviewDevice] = useState("apple")

  const visible = useMemo(() => {
    if (tabIndex === "colours" && newModel && newModelCopy) {
      if (newVariables.colours.background !== newVariablesCopy.colours.background) return true
      if (newVariables.colours.foreground !== newVariablesCopy.colours.foreground) return true
      if (newVariables.colours.label !== newVariablesCopy.colours.label) return true
      if (newVariables.colours.strip !== newVariablesCopy.colours.strip) return true
      if (eyeDropperActive) return true
    }
    return false
  }, [tabIndex, newVariables, eyeDropperActive])

  return (
    <>
      <Slide
        direction="up"            // 👈 slide in from the bottom
        in={visible}              // 👈 controlled by prop
        mountOnEnter              // unmount when hidden
        unmountOnExit
      >
        <Box sx={{ position: 'fixed', width: "410px", top: "275px", right: "380px" }}>
          <StyledPaper sx={{
            backgroundColor: (theme) => theme.palette.mode === 'dark'
              ? 'rgba(0, 0, 0, 0.95)'
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(5px)',
          }}>
            <div className="flex flex-row items-center justify-between p-1">
              <Typography variant='h4'>Colour Preview</Typography>
              <div className="flex flex-row gap-1">
                <Tooltip title="Apple preview">
                  <IconButton color={previewDevice === "apple" ? "primary" : "default"} onClick={() => setPreviewDevice("apple")}>
                    <AppleIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Google preview">
                  <IconButton color={previewDevice === "google" ? "primary" : "default"} onClick={() => setPreviewDevice("google")}>
                    <AndroidIcon />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
            <div className={previewDevice === "google" ? "google" : "apple"}>
              <div className="wrapper">
                <div className="pass">
                  {newTemplate && previewDevice === "apple" &&
                    <PassContext.Provider value={{ device: "apple", preview: true, clientId: 0, templateId: newTemplate?.id, smartixPassType, showEditableFields: false, model: newModel, template: newTemplate, variables: newVariables, passType: applePassType, subType: appleSubType, allowImageCors: eyeDropperActive }}>
                      <AppleFront />
                    </PassContext.Provider>}
                  {newTemplate && previewDevice === "google" &&
                    <PassContext.Provider value={{ device: "google", preview: true, clientId: 0, templateId: newTemplate?.id, smartixPassType, showEditableFields: false, classData: newClassData, objectData: newObjectData, variables: newVariables, passType: googlePassType, allowImageCors: eyeDropperActive }}>
                      <GoogleFront />
                    </PassContext.Provider>}
                </div>
              </div>
            </div>
          </StyledPaper>
        </Box>
      </Slide>
    </>
  )
}

const MetaData = ({ tabIndex, newTemplate, setNewTemplate, newClassData, setNewClassData, newObjectData, setNewObjectData, newMetaData, setNewMetaData, smartixPassType }) => {

  const weeks = []
  for (let i = 4; i <= 52; i++) { weeks.push(<MenuItem key={i} value={i}>{i} weeks</MenuItem>) }

  const nots = []
  for (let i = 1; i <= 20; i++) { nots.push(<MenuItem key={i} value={i}>{i}</MenuItem>) }


  if (tabIndex === "meta") return (
    <>
      <RequiredData smartixPassType={smartixPassType} newTemplate={newTemplate} setNewTemplate={setNewTemplate} newClassData={newClassData} setNewClassData={setNewClassData} />


      <Typography variant='h4' sx={{ marginTop: 5 }}>Common</Typography>
      <Typography variant="caption" sx={{ pb: 2 }}>Retention period after expiry</Typography>
      <Select
        value={newMetaData.retention_period}
        fullWidth
        onChange={e => setValue(setNewMetaData, "retention_period", e.target.value)}>
        {weeks}
      </Select>

      <p className='mt-3' />

      <Typography variant="caption" sx={{ pb: 2 }}>Maximum notifications displayed</Typography>
      <Select
        value={newMetaData.max_notifications}
        fullWidth
        onChange={e => setValue(setNewMetaData, "max_notifications", e.target.value)}>
        {nots}
      </Select>


      {smartixPassType.replace("smartix_", "") === "gift" &&
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', marginTop: 2 }}>
          <div className="flex flex-row justify-between w-full">
            <Typography>Expire GiftCard immediately<br /> when balance reaches zero?</Typography>
            <div>
              <b>No</b>
              <Switch checked={newMetaData.expire_on_zero_balance} onChange={e => setValue(setNewMetaData, "expire_on_zero_balance", e.target.checked)} />
              <b>Yes</b>
            </div>
          </div>
        </Stack>}


      <Typography variant='h4' sx={{ marginTop: 5 }}><AppleIcon fontSize="inherit" className='mr-2 mb-2' />Apple</Typography>

      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <div className="flex flex-row justify-between w-full">
          <Typography>Allow sharing</Typography>
          <div>
            <b>No</b>
            <Switch checked={newTemplate?.meta.sharing ?? false} onChange={e => setValue(setNewTemplate, "meta.sharing", e.target.checked)} />
            <b>Yes</b>
          </div>
        </div>
      </Stack>



      <Typography variant='h4' sx={{ marginTop: 5 }}><AndroidIcon fontSize="inherit" className='mr-2 mb-2' />Google Android</Typography>

      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <div className="flex flex-row justify-between w-full">
          <Typography>Allow screenshots</Typography>
          <div>
            <b>No</b>
            <Switch checked={(newObjectData?.passConstraints?.screenshotEligible ?? "ELIGIBLE") === "ELIGIBLE"} onChange={e => setValue(setNewObjectData, "passConstraints.screenshotEligible", e.target.checked ? "ELIGIBLE" : "INELIGIBLE")} />
            <b>Yes</b>
          </div>
        </div>
      </Stack>

      {false &&
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <div className="flex flex-row justify-between w-full">
            <Typography>Disable barcode on pass expiry</Typography>
            <div>
              <b>No</b>
              <Switch checked={newMetaData?.disableGoogleBarcodeOnExpiry ?? false} onChange={e => setValue(setNewMetaData, "disableGoogleBarcodeOnExpiry", e.target.checked)} />
              <b>Yes</b>
            </div>
          </div>
        </Stack>
      }
      <div className="flex flex-col w-full">
        Sharing option
        <RadioGroup
          value={newClassData?.multipleDevicesAndHoldersAllowedStatus ?? "MULTIPLE_HOLDERS"}
          name="radio-buttons-group"
          onChange={e => setValue(setNewClassData, "multipleDevicesAndHoldersAllowedStatus", e.target.value)}
          className="flex-grow flex-nowrap"
        >
          <FormControlLabel labelPlacement="start" value="MULTIPLE_HOLDERS" control={<Radio />} label="Multiple users" />
          <FormControlLabel labelPlacement="start" value="ONE_USER_ALL_DEVICES" control={<Radio />} label="One user, all devices" disabled={newMetaData?.passesIssued} />
          <FormControlLabel labelPlacement="start" value="ONE_USER_ONE_DEVICE" control={<Radio />} label="One user, single device" disabled={newMetaData?.passesIssued} />
        </RadioGroup>
        <i>NOTE: You cannot change to a <b>one user</b> sharing option after issuing <b>any</b> passes (including test passes)</i>
      </div>
    </>
  )
}

const Colours = ({ tabIndex, newModel, setEyeDropperActive, applePassType, template, newVariables, setNewVariables }) => {

  const [colours, setColours] = useState({ ...newModel })
  const [logoTextSource, setLogoTextSource] = useState("foregroundColor")
  const [activeEyedropField, setActiveEyedropField] = useState(null)
  const stayOpenRef = useRef(false)
  const eyeDropperControllerRef = useRef(null)

  useEffect(() => {
    const onKeyDown = e => {
      if (e.key !== "Escape") return
      eyeDropperControllerRef.current?.abort()
      eyeDropperControllerRef.current = null
      setActiveEyedropField(null)
      setEyeDropperActive(false)
    }

    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [setEyeDropperActive])

  const pickFromScreen = useCallback(async (field, stayOpen = false) => {
    if (!("EyeDropper" in window)) {
      alert("Eyedropper not supported in this browser")
      return
    }

    if (activeEyedropField === field) {
      stayOpenRef.current = false
      eyeDropperControllerRef.current?.abort()
      eyeDropperControllerRef.current = null
      setActiveEyedropField(null)
      setEyeDropperActive(false)
      document.body.style.cursor = ""
      return
    }

    setActiveEyedropField(field)
    setEyeDropperActive(true)
    stayOpenRef.current = stayOpen

    // Wait for paint (double rAF is safest for transitions)
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)))
    await new Promise((r) => setTimeout(r, 300))
    document.body.style.cursor = "copy" // show copy cursor

    try {
      do {
        const controller = new AbortController()
        eyeDropperControllerRef.current = controller

        const { sRGBHex } = await new window.EyeDropper().open({ signal: controller.signal })
        setColour(field, sRGBHex)
      } while (stayOpenRef.current)
    } catch (err) {
      // user canceled / failed
    } finally {
      stayOpenRef.current = false
      eyeDropperControllerRef.current = null
      setActiveEyedropField(null)
      document.body.style.cursor = "" // restore default      
      setEyeDropperActive(false)
    }
  }, [activeEyedropField, setEyeDropperActive])

  const isEventBg = useMemo(() => {

    if (applePassType !== "eventTicket") return false

    const bgImage = appleDataSelector({ template, variables: newVariables, path: "meta.images.background" })

    if (bgImage) {
      return true
    }

    return false
  }, [template, newVariables, applePassType])

  useEffect(() => {
    if (newVariables) {
      setColours({ ...newVariables.colours })

      if (isEventBg) {
        setLogoTextSource("labelColor")
      } else {
        const colourSource = logoTextColourSource({ variables: newVariables })
        setLogoTextSource(colourSource)
      }
    }
  }, [newVariables, isEventBg])


  const setColour = (field, colour) => {
    const normalisedColour = /^#[0-9a-fA-F]{6}$/.test(colour)
      ? colour
      : /^[0-9a-fA-F]{6}$/.test(colour)
        ? `#${colour}`
        : null

    setColours(prev => { return { ...prev, [field]: colour } })

    if (normalisedColour) { // Only save to model if colour is valid
      setNewVariables(prev => {
        let n = { ...prev }
        _.set(n, "colours." + field, colour)
        return n
      })

      if (!isEventBg) {
        const colourSource = logoTextColourSource({ variables: newVariables })
        setLogoTextSource(colourSource)
      }
    }
  }

  if (tabIndex === "colours") return (
    <div className='colours'>
      <div>
        <Typography variant="h6" sx={{ p: 2 }}>
          <div className="flex flex-row w-full justify-between items-center">
            <div>
              <div>Background Colour</div>
              <Typography sx={{ color: "lightgray" }}>Apple & Google</Typography>
            </div>
            <TextField value={colours?.background ?? "#ffffff"} sx={{ width: 100 }} onChange={e => setColour("background", e.target.value)} />
            <button type="button" onClick={e => pickFromScreen("background", e.shiftKey)}><ColorizeIcon /></button>
          </div>
        </Typography>
        <HexColorPicker color={colours?.background ?? "#ffffff"} onChange={c => setColour("background", c)} />
        <div className='mt-2'>Logo text colour source: <span style={{ color: logoTextSource === "ambiguous" ? "red" : "" }}>{logoTextSource}</span></div>
      </div>

      <div className="mt-10">
        <Typography variant="h6" sx={{ p: 2 }}>
          <div className="flex flex-row w-full justify-between items-center">
            <div>
              <div>Text Colour</div>
              <Typography sx={{ color: "lightgray" }}>Apple only</Typography>
            </div>
            <TextField value={colours?.foreground ?? "#ffffff"} sx={{ width: 100 }} onChange={e => setColour("foreground", e.target.value)} />
            <button type="button" onClick={e => pickFromScreen("foreground", e.shiftKey)}><ColorizeIcon /></button>
          </div>
        </Typography>
        <HexColorPicker color={colours?.foreground ?? "#ffffff"} onChange={c => setColour("foreground", c)} />
      </div>
      <div>
        <Typography variant="h6" sx={{ p: 2 }}>
          <div className="flex flex-row w-full justify-between items-center">
            <div>
              <div>Label Colour</div>
              <Typography sx={{ color: "lightgray" }}>Apple only</Typography>
            </div>
            <TextField value={colours?.label ?? "#ffffff"} sx={{ width: 100 }} onChange={e => setColour("label", e.target.value)} />
            <button type="button" onClick={e => pickFromScreen("label", e.shiftKey)}><ColorizeIcon /></button>
          </div>
        </Typography>
        <HexColorPicker color={colours?.label ?? "#ffffff"} onChange={c => setColour("label", c)} />
      </div>

      <div className="mt-10">
        <Typography variant="h6" sx={{ p: 2 }}>
          <div className="flex flex-row w-full justify-between items-center">
            <div>
              <div>Strip Colour</div>
              <Typography sx={{ color: "lightgray" }}>Apple only</Typography>
            </div>
            <TextField value={colours?.strip ?? "#ffffff"} sx={{ width: 100 }} onChange={e => setColour("strip", e.target.value)} />
            <button type="button" onClick={e => pickFromScreen("strip", e.shiftKey)}><ColorizeIcon /></button>
          </div>
        </Typography>
        <HexColorPicker color={colours?.strip ?? "#ffffff"} onChange={c => setColour("strip", c)} />
      </div>
    </div>
  )
}

const Apps = ({ tabIndex, newTemplate, setNewTemplate, newClassData, setNewClassData, newObjectData, setNewObjectData, googlePassType }) => {

  const { apiSource, apiKey } = useContext(AppContext)

  const [searchTerm, setSearchTerm] = useState("")
  const [countryCode, setCountryCode] = useState("gb")

  const [appleAppList, setAppleAppList] = useState([])
  const [selectedAppleApp, setSelectedAppleApp] = useState()

  const [googleAppList, setGoogleAppList] = useState([])
  const [selectedGoogleApp, setSelectedGoogleApp] = useState()

  const googleSourceData = useMemo(() => googlePassType === "generic" ? newObjectData : newClassData)
  const googleSourceDataSetter = useMemo(() => googlePassType === "generic" ? setNewObjectData : setNewClassData)

  const search = async () => {
    try {
      const appleResult = await searchAppleAppstore({ apiSource, apiKey, countryCode, searchTerm })
      // TODO handler error

      if (appleResult.ok) {
        const apps = appleResult.apps.filter(i => i.primaryGenreName !== "Games")
        setSelectedAppleApp(apps[0])
        setAppleAppList(apps)
      } else {
        enqueueSnackbar({ message: appleResult.error, variant: "error" })
      }


      let googleResult = await searchGooglePlaystore({ apiSource, apiKey, searchTerm })
      if (googleResult.ok) {
        // TODO handler error
        setSelectedGoogleApp(googleResult.apps[0])
        setGoogleAppList(googleResult.apps)
      } else {
        enqueueSnackbar({ message: googleResult.error, variant: "error" })
      }
    }
    catch (err) {
      console.error(err)
    }
  }

  const searchEnterKey = e => {
    if (e.code === "Enter") search()
  }

  const removeLinkedAppleApp = () => {
    setNewTemplate(prev => {
      let temp = { ...prev }
      _.unset(temp, "meta.appLink")
      return temp
    })
  }

  const removeLinkedGoogleApp = () => {
    googleSourceDataSetter(prev => {
      let temp = { ...prev }
      delete temp.appLinkData?.androidAppLinkInfo
      delete temp.appLinkData?.displayText
      return temp
    })
  }

  const removeGoogleWebApp = () => {
    googleSourceDataSetter(prev => {
      let temp = { ...prev }
      delete temp.appLinkData.webAppLinkInfo
      return temp
    })
  }

  const assignSelectedAppleApp = () => {
    const selectedAppEssentialData = {
      id: selectedAppleApp.trackId,
      seller: selectedAppleApp.sellerName,
      title: selectedAppleApp.trackName,
      icon: selectedAppleApp.artworkUrl60
    }

    setNewTemplate(prev => {
      let temp = { ...prev }
      _.set(temp, "meta.appLink", selectedAppEssentialData)
      return temp
    })

    setSearchTerm("")
    setAppleAppList([])
  }

  const assignSelectedGoogleApp = () => {
    let selectedAppEssentialData = {
      ...googleSourceData.appLinkData,
      androidAppLinkInfo: {
        appTarget: {
          packageName: selectedGoogleApp.appId
        }
      },
      displayText: {
        defaultValue: {
          value: selectedGoogleApp.title
        }
      },
      seller: selectedGoogleApp.developer,
      title: selectedGoogleApp.title,
      icon: selectedGoogleApp.icon
    }

    googleSourceDataSetter(prev => {
      let temp = { ...prev }
      temp.appLinkData = selectedAppEssentialData
      return temp
    })
  }


  const LinkedAppleApp = ({ newTemplate }) => {

    if (newTemplate.meta?.appLink) return (
      <div className="flex flex-row gap-3 mb-5 p-4" style={{ border: "1px solid" }}>
        <img src={newTemplate.meta.appLink.icon} />
        <div className="flex flex-col gap-3">
          <div>{newTemplate.meta.appLink.seller}</div>
          <div>{newTemplate.meta.appLink.title}</div>
        </div>
      </div>
    )

    return (
      <div>No app currently assigned</div>
    )
  }

  const LinkedGoogleNativeApp = ({ sourceData }) => {

    if (sourceData.appLinkData?.androidAppLinkInfo) {
      return (
        <div className="flex flex-row gap-3 mb-5 p-4" style={{ width: 420, border: "1px solid black" }}>
          <img src={sourceData.appLinkData.icon} style={{ height: 60, width: 60 }} />
          <div className="flex flex-col gap-3">
            <div>{sourceData.appLinkData.seller}</div>
            <div>{sourceData.appLinkData.title}</div>
            <div>{sourceData.appLinkData.androidAppLinkInfo.appTarget.packageName}</div>
          </div>
        </div>
      )
    } else {
      return <div>No linked native app</div>
    }
  }

  const LinkedGoogleWebApp = ({ sourceData, setWebApp }) => {

    const uri = sourceData.appLinkData?.webAppLinkInfo ? sourceData.appLinkData?.webAppLinkInfo?.appTarget.targetUri.uri : ""
    const description = sourceData.appLinkData?.webAppLinkInfo ? sourceData.appLinkData?.webAppLinkInfo?.appTarget.targetUri.description : ""

    return (
      <>
        <TextField
          label="Web URL"
          fullWidth
          value={uri}
          slotProps={{ inputLabel: { shrink: true } }}
          type="search"
          onChange={e => setWebApp("uri", e.target.value)}
          sx={{ marginBottom: 2 }}
        />

        <TextField
          label="Description"
          fullWidth
          value={description}
          slotProps={{ inputLabel: { shrink: true } }}
          type="search"
          onChange={e => setWebApp("description", e.target.value)}
        />
      </>
    )
  }

  if (tabIndex === "apps") return (
    <>
      <Typography variant="h6" sx={{ p: 2 }}>
        Search for an application
      </Typography>

      <Select
        value={countryCode}
        fullWidth
        onChange={e => setCountryCode(e.target.value)}>
        {countries.map(i => <MenuItem key={i.id} value={i.id}>{i.name}</MenuItem>)}
      </Select>

      <div className="flex flex-row justify-between mb-10 mt-3 items-center">
        <TextField
          label="search term"
          type="search"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          fullWidth
          onKeyUp={searchEnterKey}
        />
        <Button onClick={search} variant="outlined" style={{ marginLeft: 15 }}>Search</Button>
      </div>



      <Typography variant='h4'><AppleIcon fontSize="inherit" className='mr-2 mb-2' />Apple</Typography>

      <Typography variant="h6" sx={{ p: 2 }}>
        Linked Native App
      </Typography>

      {newTemplate?.meta.appLink ?
        <div className="flex flex-col gap-5">
          <div className="flex-grow">
            <LinkedAppleApp newTemplate={newTemplate} />
          </div>
          <Button onClick={removeLinkedAppleApp} color="error" variant="outlined" sx={{ height: 40 }}>Remove App</Button>
        </div>
        :
        <div className='ml-10 italic'>none selected</div>
      }

      {!newTemplate?.meta.appLink && appleAppList.length > 0 &&
        <div className="flex flex-col gap-5 mb-10">
          <div className="flex-grow">
            <Select
              value={selectedAppleApp}
              fullWidth
              onChange={e => setSelectedAppleApp(e.target.value)}>
              {appleAppList.map(i => <MenuItem key={i.trackId} value={i}>
                <div className="flex flex-row gap-3 mb-5">
                  <img src={i.artworkUrl60} />
                  <div className="flex flex-col gap-3">
                    <div>{i.sellerName}</div>
                    <div>{i.trackName}</div>
                  </div>
                </div>
              </MenuItem>)}
            </Select>
          </div>
          <Button onClick={assignSelectedAppleApp} variant="contained" sx={{ height: 40 }}>Assign App</Button>
        </div>
      }

      <Typography variant='h4' sx={{ marginTop: 5 }}><AndroidIcon fontSize="inherit" className='mr-2 mb-2' />Google Android</Typography>


      {!googleSourceData.appLinkData?.androidAppLinkInfo && googleAppList.length > 0 &&
        <div className="flex flex-col gap-5 mb-10">
          <div className="flex-grow">
            <Select
              value={selectedGoogleApp}
              fullWidth
              onChange={e => setSelectedGoogleApp(e.target.value)}>
              {googleAppList.map(i => <MenuItem key={i.appId} value={i}>
                <div className="flex flex-row gap-3 mb-5">
                  <img src={i.icon} style={{ height: 60, width: 60 }} />
                  <div className="flex flex-col gap-3">
                    <div>{i.developer}</div>
                    <div>{i.title}</div>
                  </div>
                </div>
              </MenuItem>)}
            </Select>
          </div>
          <Button onClick={assignSelectedGoogleApp} variant="outlined" sx={{ height: 40 }}>Assign App</Button>
        </div>
      }

      <Typography variant="h6" sx={{ p: 2 }}>
        Linked Native App
      </Typography>

      {googleSourceData.appLinkData?.androidAppLinkInfo ?
        <div className="flex flex-col gap-5">
          <div className="flex-grow">
            <LinkedGoogleNativeApp sourceData={googlePassType === "generic" ? newObjectData : newClassData} />
          </div>
          <Button onClick={removeLinkedGoogleApp} color="error" variant="outlined" sx={{ height: 40 }}>Remove Native App</Button>
        </div>
        :
        <div className='ml-10 italic'>none selected</div>
      }


      <Typography variant="h6" sx={{ p: 2 }}>
        Linked Web App
      </Typography>

      <div className='flex flex-col gap-2 w-full'>
        <LinkedGoogleWebApp sourceData={googleSourceData} setWebApp={googleSourceDataSetter} />
        <Button onClick={removeGoogleWebApp} color="error" variant="outlined">Remove Web App</Button>
      </div>
    </>
  )
}


const RequiredData = ({ smartixPassType, newTemplate, setNewTemplate, newClassData, setNewClassData }) => {

  switch (smartixPassType.replace("smartix_", "").replace("_bg", "")) {
    case "event": return <RequiredEventData newTemplate={newTemplate} setNewTemplate={setNewTemplate} newClassData={newClassData} setNewClassData={setNewClassData} />
    case "offer": return <RequiredOfferData newTemplate={newTemplate} setNewTemplate={setNewTemplate} newClassData={newClassData} setNewClassData={setNewClassData} />
  }
}

const RequiredEventData = ({ newClassData, setNewClassData }) => {

  const [eventId, setEventId] = useState(newClassData.eventId)

  useEffect(() => {
    setNewClassData(prev => {
      return { ...prev, eventId }
    })
  }, [eventId])

  return (
    <>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Event Data
      </Typography>

      <TextField
        label="Event ID (required)"
        fullWidth
        value={eventId}
        slotProps={{ inputLabel: { shrink: true } }}
        type="search"
        onChange={e => setEventId(e.target.value)}
      />

    </>
  )
}

const RequiredOfferData = ({ newClassData, setNewClassData }) => {

  const [provider, setProvider] = useState(newClassData.provider)
  const [redemptionChannel, setRedemptionChannel] = useState(newClassData.redemptionChannel)

  useEffect(() => {
    setNewClassData(prev => {
      return { ...prev, provider }
    })
  }, [provider])

  useEffect(() => {
    setNewClassData(prev => {
      return { ...prev, redemptionChannel }
    })
  }, [redemptionChannel])

  return (
    <>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Offer Data
      </Typography>

      <TextField
        label="Provider (required)"
        fullWidth
        value={provider}
        slotProps={{ inputLabel: { shrink: true } }}
        type="search"
        onChange={e => setProvider(e.target.value)}
        helperText="This is required for Google, but is not shown on the pass."
      />

      <p>Redemption Channel</p>

      <Select
        value={redemptionChannel}
        fullWidth
        onChange={e => setRedemptionChannel(e.target.value)}>
        <MenuItem value="ONLINE">Online</MenuItem>
        <MenuItem value="INSTORE">In Store</MenuItem>
        <MenuItem value="BOTH">Both</MenuItem>
      </Select>
      <p className='ml-5 text-xs'>This is required for Google, but is not shown on the pass.</p>
    </>
  )
}
